import {
  useRef, useState, useCallback, useEffect 
} from 'react';
import {
  Camera, SwitchCamera, X, Check, RotateCcw 
} from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/classnames';

interface CameraCaptureProps {
  readonly onCapture: (imageData: string, mediaType: 'image/jpeg' | 'image/png') => void;
  readonly onCancel?: () => void;
  readonly className?: string;
  readonly aspectRatio?: 'square' | 'portrait' | 'landscape';
  readonly quality?: number;
}

type FacingMode = 'user' | 'environment';

export function CameraCapture({
  onCapture,
  onCancel,
  className,
  aspectRatio = 'landscape',
  quality = 0.85,
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>('environment');
  const [error, setError] = useState<string | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const aspectRatioClass = {
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
    landscape: 'aspect-video',
  }[aspectRatio];

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const startStream = useCallback(async () => {
    try {
      setError(null);
      stopStream();

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsStreaming(true);
      }

      // Check for multiple cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to access camera';
      setError(message);
      setIsStreaming(false);
    }
  }, [facingMode, stopStream]);

  useEffect(() => {
    startStream();
    return stopStream;
  }, [startStream, stopStream]);

  const handleCapture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    const imageData = canvas.toDataURL('image/jpeg', quality);
    setCapturedImage(imageData);
    stopStream();
  }, [quality, stopStream]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    startStream();
  }, [startStream]);

  const handleConfirm = useCallback(() => {
    if (capturedImage) {
      // Extract base64 data without the data URL prefix
      const base64Data = capturedImage.split(',')[1];
      onCapture(base64Data, 'image/jpeg');
    }
  }, [capturedImage, onCapture]);

  const handleSwitchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  }, []);

  if (error) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-4 p-8 bg-gray-100 rounded-lg', aspectRatioClass, className)}>
        <Camera className="w-12 h-12 text-gray-400" />
        <p className="text-sm text-gray-600 text-center">{error}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={startStream}>
            Try Again
          </Button>
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (capturedImage) {
    return (
      <div className={cn('relative rounded-lg overflow-hidden', className)}>
        <img
          src={capturedImage}
          alt="Captured"
          className={cn('w-full object-cover', aspectRatioClass)}
        />
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
          <Button
            variant="outline"
            size="lg"
            onClick={handleRetake}
            className="bg-white/90 backdrop-blur-sm"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Retake
          </Button>
          <Button
            size="lg"
            onClick={handleConfirm}
            className="bg-green-600 hover:bg-green-700"
          >
            <Check className="w-5 h-5 mr-2" />
            Use Photo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative rounded-lg overflow-hidden bg-black', className)}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn('w-full object-cover', aspectRatioClass)}
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Camera controls overlay */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
          >
            <X className="w-5 h-5" />
          </Button>
        )}

        <Button
          size="lg"
          onClick={handleCapture}
          disabled={!isStreaming}
          className="w-16 h-16 rounded-full bg-white hover:bg-gray-100 text-gray-900"
        >
          <Camera className="w-8 h-8" />
        </Button>

        {hasMultipleCameras && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSwitchCamera}
            className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
          >
            <SwitchCamera className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Loading indicator */}
      {!isStreaming && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
        </div>
      )}
    </div>
  );
}
